import DOMPurify from 'dompurify';

/**
 * Truncate text to approximately maxLength characters at word boundaries
 * and strip HTML tags to ensure plain text output
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length (default: 300)
 * @returns {string} Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 300) => {
  if (!text) return '';

  // First, strip HTML tags
  const strippedText = text.replace(/<[^>]*>/g, '');

  // If the text is already short enough, return it
  if (strippedText.length <= maxLength) {
    return strippedText;
  }

  // Find the last word boundary before maxLength
  const truncated = strippedText.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(' ');

  // If we found a space, truncate at that point
  // Otherwise, use the full truncated string (edge case for very long words)
  const finalText = lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated;

  return finalText + '...';
};

/**
 * Alias for truncateText for backwards compatibility
 */
export const truncateSummary = truncateText;

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - The HTML content to sanitize
 * @returns {string} Sanitized HTML
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html);
};

/**
 * Get word count from text content
 * @param {string} text - The text to count words in
 * @returns {number} Word count
 */
export const getWordCount = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).length;
};
